<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="returnReceiveTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <div>
            <h5 class="modal-title" id="returnReceiveTitle">
              Rücksendung annehmen {{ slip?.slip_number ?? '' }}
            </h5>
            <div v-if="slip" class="return-subtitle">
              von {{ slip.servicepartner.description }} · {{ formatDate(slip.creation_timestamp) }}
            </div>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen"></button>
        </div>

        <div v-if="slip" class="modal-body">
          <div v-if="errorMessage" class="error">{{ errorMessage }}</div>

          <div v-if="result" class="return-result">
            <div><strong>Angenommen ({{ result.received_count }}):</strong> {{ result.received.join(', ') || '—' }}</div>
            <div v-if="result.still_open_count > 0" class="return-result-open">
              <strong>Fehlt noch ({{ result.still_open_count }}):</strong> {{ result.still_open.join(', ') }}
            </div>
          </div>

          <template v-else>
            <!-- Abscannen wie überall: Handscanner tippt Nummer plus Enter, der
                 Cursor bleibt stehen. -->
            <div class="return-scan">
              <div class="return-scan-field">
                <label for="return-scan" class="form-label">R2-Nummer scannen</label>
                <input
                  id="return-scan"
                  ref="scanEl"
                  v-model="scanInput"
                  type="text"
                  class="form-control ticket-mono"
                  autocomplete="off"
                  spellcheck="false"
                  @keydown.enter.prevent="onScan"
                />
              </div>
              <div class="return-scan-counter">
                <strong>{{ scanned.size }}</strong> / {{ offene.length }} gescannt
              </div>
            </div>
            <div v-if="scanMessage" class="return-scan-message">{{ scanMessage }}</div>

            <table class="table table-sm align-middle">
              <thead>
                <tr>
                  <th scope="col" style="width: 34px;"></th>
                  <th scope="col">R2-Nr</th>
                  <!-- Hersteller, Gerät, Farbe und Speicher in einer Spalte —
                       derselbe Text wie im Versanddialog und auf dem Papier. Wer
                       ein Paket auspackt, soll das Gerät genauso lesen wie der,
                       der es gepackt hat. -->
                  <th scope="col">Gerät</th>
                  <th scope="col">IMEI / SN</th>
                  <th scope="col">Ergebnis</th>
                  <th scope="col">Zustand</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="device in slip.devices" :key="device.stock_device_id">
                  <td>
                    <span v-if="device.received_timestamp" title="schon angenommen">✓</span>
                    <span v-else-if="scanned.has(device.stock_device_id)" class="return-tick">✓</span>
                  </td>
                  <td class="ticket-mono">{{ device.stock_number }}</td>
                  <td>{{ device.description || '—' }}</td>
                  <td class="ticket-mono">{{ device.imei_sn ?? '—' }}</td>
                  <td>
                    <PartnerOutcomeBadge :outcome="device.outcome" :grading="device.grading" />
                  </td>
                  <td>
                    <span v-if="device.received_timestamp" class="text-muted">
                      angenommen {{ formatDate(device.received_timestamp) }}
                    </span>
                    <span v-else class="text-muted">unterwegs</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <p v-if="scanned.size < offene.length" class="return-hint">
              Nicht gescannte Geräte bleiben offen — die Rücksendung steht dann weiter im Dashboard.
            </p>
          </template>
        </div>

        <div class="modal-footer">
          <template v-if="result">
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal">Schließen</button>
          </template>
          <template v-else>
            <button type="button" class="btn btn-secondary" :disabled="busy" data-bs-dismiss="modal">Abbrechen</button>
            <button type="button" class="btn btn-primary" :disabled="busy || scanned.size === 0" @click="onReceive">
              {{ busy ? 'Wird gebucht …' : 'Annehmen' }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import PartnerOutcomeBadge from './PartnerOutcomeBadge.vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import { receiveReturnSlip, type ReceiveResult, type ReturnSlip } from '../services/returnSlips';

/**
 * Eine Rücksendung annehmen: die Geräte aus dem Paket abscannen.
 *
 * Ein Teilabschluss ist gewollt — ein Paket, in dem ein Gerät fehlt, ist genau
 * der Fall, der auffallen muss. Was nicht gescannt wird, bleibt offen, und die
 * Rücksendung steht weiter im Dashboard.
 *
 * `open(slip)` löst mit `true` auf, wenn etwas gebucht wurde: dann lädt der
 * Aufrufer seine Liste neu.
 */
const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
const scanEl  = useTemplateRef<HTMLInputElement>('scanEl');
let   modal: Modal | null = null;
let   resolver: ((changed: boolean) => void) | null = null;
let   changed = false;

const slip         = ref<ReturnSlip | null>(null);
const scanned      = ref<Set<number>>(new Set());
const scanInput    = ref('');
const scanMessage  = ref<string | null>(null);
const busy         = ref(false);
const errorMessage = ref<string | null>(null);
const result       = ref<ReceiveResult | null>(null);

/** Nur die noch nicht angenommenen zählen für den Zähler. */
const offene = computed(() => (slip.value?.devices ?? []).filter(d => d.received_timestamp === null));

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
    modalEl.value.addEventListener('shown.bs.modal', focusScan);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modalEl.value?.removeEventListener('shown.bs.modal', focusScan);
  modal?.dispose();
  modal = null;
});

function open(item: ReturnSlip): Promise<boolean> {
  slip.value         = item;
  scanned.value      = new Set();
  scanInput.value    = '';
  scanMessage.value  = null;
  errorMessage.value = null;
  result.value       = null;
  busy.value         = false;
  changed            = false;
  return new Promise<boolean>(resolve => {
    resolver = resolve;
    modal?.show();
  });
}

function onHidden(): void {
  resolver?.(changed);
  resolver = null;
  slip.value = null;
}

function focusScan(): void {
  void nextTick(() => scanEl.value?.focus());
}

/** Trifft mit und ohne Präfix: der Scanner liefert „R2-5481", von Hand tippt man „5481". */
function onScan(): void {
  const raw = scanInput.value.trim();
  scanInput.value = '';
  if (raw === '') return;

  const needle = normalize(raw);
  const hit = offene.value.find(d => normalize(d.stock_number) === needle);

  if (hit === undefined) {
    scanMessage.value = 'Diese Nummer steht nicht offen auf dieser Rücksendung.';
    return;
  }
  if (scanned.value.has(hit.stock_device_id)) {
    scanMessage.value = 'Diese Nummer ist bereits gescannt.';
    return;
  }
  scanned.value = new Set(scanned.value).add(hit.stock_device_id);
  scanMessage.value = null;
}

function normalize(value: string): string {
  return value.trim().toUpperCase().replace(/^(R2|RD)-/, '');
}

async function onReceive(): Promise<void> {
  if (slip.value === null || busy.value || scanned.value.size === 0) return;

  errorMessage.value = null;
  busy.value         = true;
  try {
    result.value = await receiveReturnSlip(slip.value.id, [...scanned.value]);
    changed = result.value.received_count > 0;
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : 'Die Annahme konnte nicht gebucht werden.';
  } finally {
    busy.value = false;
  }
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('de-AT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(d);
}

defineExpose({ open });
</script>

<style scoped>
.return-subtitle {
  font-size: 13px;
  color: #6c757d;
  margin-top: 2px;
}

.return-scan {
  display: flex;
  align-items: flex-end;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.return-scan-field { flex: 1 1 260px; }

.return-scan-counter {
  padding: 8px 14px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.03);
  white-space: nowrap;
  font-size: 13px;
  color: #6c757d;
}

.return-scan-counter strong {
  font-size: 18px;
  color: #212529;
}

.return-scan-message {
  margin-bottom: 12px;
  padding: 8px 13px;
  border-radius: 8px;
  background: rgba(247, 166, 0, 0.12);
  color: #8a5c00;
  font-size: 14px;
}

.return-tick { color: #146c43; font-weight: 700; }

.return-result {
  padding: 12px 16px;
  background: rgba(25, 135, 84, 0.10);
  border: 1px solid rgba(25, 135, 84, 0.20);
  border-radius: 8px;
  color: #146c43;
}

.return-result-open {
  margin-top: 4px;
  font-size: 13px;
  color: #8a5c00;
}

.return-hint {
  margin: 12px 0 0;
  font-size: 13px;
  color: #6c757d;
}
</style>
